import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useLoyaltyAccount from 'loyalty/hooks/useLoyaltyAccount'
import TextButton from 'LuxKit/Button/TextButton'
import Flag from 'LuxKit/Flag'
import LineSyncIcon from 'LuxKit/Icons/line/LineSyncIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import switchRegion from 'lib/geo/switchRegion'
import config from 'constants/config'

const MemberText = styled.span`
  text-transform: none;
  font-weight: normal;
  white-space: nowrap;
`

const MobileContainer = styled(Group)`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const InlineButton = styled(TextButton)`
  flex: 0 0 auto;
  align-self: flex-start; /* or center */
  width: auto;
`

const HeroSection = styled.div`
  padding: ${rem(48)} 0;

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }

  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }

  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }

  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

const InlineFlag = styled(Flag)`
  margin-left: ${rem(8)};
  vertical-align: middle;
`

function LoyaltyAccountLandingPageHeroNonMemberRegion() {
  const [tier, tierName] = useCurrentLoyaltyTier()
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const loyaltyRegion = useAppSelector(state => state.loyalty.account.data?.region)
  const loyaltyCurrency = useMemo(() => {
    return config.regions.find(r => r.code === loyaltyRegion)?.currencyCode
  }, [loyaltyRegion])
  const account = useLoyaltyAccount().data!

  const handleSwitchRegion = useCallback(() => {
    if (!loyaltyRegion) return
    switchRegion(loyaltyRegion)
  }, [loyaltyRegion])

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <HeroSection className={`tier-${tier}`}>
      <LayoutContainer variant="medium">
        <div>
          <Group direction="vertical" gap={8}>
            <Group
              direction="horizontal"
              gap={12}
            >
              <LoyaltyProgramNameLogo width={104} />
              <LoyaltyTierLabel kind="primary" tier={tier} format="uppercase">
                {tierName}
                <MemberText> · Member {account.accountNumber}</MemberText>
                {loyaltyRegion && (
                  <InlineFlag size="XS" countryCode={loyaltyRegion} />
                )}
              </LoyaltyTierLabel>

            </Group>
            <CSSBreakpoint min="tablet">
              <Group direction="horizontal" verticalAlign="end" horizontalAlign="space-between" desktopGap={40}>
                <div>
                  <Heading variant="heading1">
                    {firstName && <><i>{firstName}</i>, it looks like you're overseas.</>}
                    {!firstName && <>It looks like you're overseas.</>}
                  </Heading>
                  {loyaltyCurrency && <BodyText variant="large">
                    You'll need to switch to {loyaltyCurrency} and redeem your member benefits.
                  </BodyText>}
                </div>
                {loyaltyCurrency && <TextButton kind="primary" size="medium" type="submit" endIcon={<LineSyncIcon />} onClick={handleSwitchRegion}>Switch to {loyaltyCurrency} </TextButton>}
              </Group>
            </CSSBreakpoint>
            <MobileContainer direction="vertical" gap={16}>
              <Heading variant="heading1" align="start">
                {firstName && <><i>{firstName}</i>, it looks like you're overseas.</>}
                {!firstName && <>It looks like you're overseas.</>}
              </Heading>
              {loyaltyCurrency && <BodyText variant="large" align="start">
                You'll need to switch to {loyaltyCurrency} and redeem your member benefits.
              </BodyText>}
              {loyaltyCurrency && <InlineButton kind="primary" size="medium" endIcon={<LineSyncIcon />} onClick={handleSwitchRegion}>Switch to {loyaltyCurrency} </InlineButton>}
            </MobileContainer>
          </Group>
        </div>
      </LayoutContainer>
    </HeroSection>
  </ProductPaletteProvider>
}

export default LoyaltyAccountLandingPageHeroNonMemberRegion
